/* eslint-disable hh/comment-budget */
import { get } from 'lodash'
import { useCallback, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import {
  useCreateConfiguredServiceEventsMutation,
  useLazyFetchConfiguredServiceDetailsQuery,
  useUpdateConfiguredServiceMutation,
} from 'api/configured-service/configuredServiceCrud'
import {
  useCreateDraftConfiguredServiceMutation,
  usePublishDraftConfiguredServiceMutation,
} from 'api/configured-service/draftPricingCSCrud'
import { useServiceDetailsFormSubmission } from 'components/configured-service/accordions/ServiceDetailsAccordion/useServiceDetailsFormSubmission'
import { findRecurrenceOptionByKey } from 'components/configured-service/EditServiceRecurrenceDialog/utils/recurrenceOptionsHelper'
import { selectIsInventoryPlacementsEnabled } from 'data/tenant/tenantSettingsSelectors'
import { formatDateToBEFormatDateFns } from 'utils/date'
import { handleError } from 'utils/error'
import { formatDollarsToCents } from 'utils/price'

/**
 * Submission hook for Create Draft Configured Service Dialog
 * Encapsulates logic for creating and publishing draft services, including
 * optional event creation for on-request services and recurrence updates.
 */
export function useCreateDraftCSSubmit({
  methods,
  canEditPrice,
  isSelectedPricedServiceOnRequest,
  selectedPricedService,
  accountId,
  recurrenceOptions,
}) {
  const { watch } = methods
  const onRequestAction = watch('onRequestAction')
  const isInventoryPlacementsEnabled = useSelector(selectIsInventoryPlacementsEnabled, shallowEqual)

  const [createDraftConfiguredService, { isLoading: isCreating }] = useCreateDraftConfiguredServiceMutation()
  const [publishDraftServiceMutation, { isLoading: isPublishing }] = usePublishDraftConfiguredServiceMutation()
  const [createCSEvents] = useCreateConfiguredServiceEventsMutation()
  const [updateConfiguredService, { isLoading: isUpdating }] = useUpdateConfiguredServiceMutation()
  const [fetchConfiguredService] = useLazyFetchConfiguredServiceDetailsQuery()
  const { formatFormData } = useServiceDetailsFormSubmission(null)

  const [isComplexSubmitting, setIsComplexSubmitting] = useState(false)
  const onSubmitHandler = useCallback(
    async data => {
      try {
        const { location, serviceStartDate, pricedServiceId, quantity, price, tags } = data
        const isOnRequest = isSelectedPricedServiceOnRequest
        const payload = {
          accountId,
          locationId: get(location, 'id'),
          pricedServiceId,
          ...(!isOnRequest &&
            serviceStartDate && {
              startDate: formatDateToBEFormatDateFns(serviceStartDate),
            }),
          quantity,
          ...(isInventoryPlacementsEnabled && { createPlaceholders: true }),
          ...(canEditPrice && { servicePriceCents: formatDollarsToCents(price) }),
          tagIds: tags?.map(tag => tag.id) || [],
        }
        return createDraftConfiguredService(payload).unwrap()
      } catch (error) {
        handleError(error)
        return null
      }
    },
    [accountId, canEditPrice, isInventoryPlacementsEnabled, isSelectedPricedServiceOnRequest, createDraftConfiguredService]
  )

  const createDraftService = useCallback(data => onSubmitHandler(data), [onSubmitHandler])

  const createAndPublishDraftService = useCallback(
    async data => {
      setIsComplexSubmitting(true)
      try {
        const isOnRequest = isSelectedPricedServiceOnRequest
        const hasDate = !!data?.serviceStartDate
        const hasAction = !!onRequestAction

        const result = await onSubmitHandler(data)
        if (result) {
          const publishResult = await publishDraftServiceMutation({ id: result.id }).unwrap()

          if (isOnRequest && hasDate && hasAction) {
            await createCSEvents({
              id: publishResult.configuredServiceId,
              body: {
                actionId: onRequestAction.id,
                serviceDate: formatDateToBEFormatDateFns(data.serviceStartDate),
              },
              skipWorkOrderInvalidation: true,
            }).unwrap()
          } else if (!isOnRequest) {
            // Capture form values synchronously before the dialog closes and resets
            // the form. Calling methods.getValues() inside .then() would return empty
            // values because the dialog unmounts (and resets the form) before the
            // fetchConfiguredService promise resolves, resulting in recurrence/amount
            // being wiped and no work orders being created.
            const formValues = methods.getValues()
            const serviceIntervalObj = findRecurrenceOptionByKey(recurrenceOptions, formValues.serviceInterval) || null

            await fetchConfiguredService({ id: publishResult.configuredServiceId })
              .unwrap()
              .then(fullConfiguredService => {
                const updateData = {
                  startDate: formValues.serviceStartDate,
                  price: formValues.price,
                  recurrenceFrequency: Number(formValues.recurrenceFrequency || '1'),
                  recurrenceInterval: serviceIntervalObj?.recurrenceInterval,
                  recurrence: serviceIntervalObj || null,
                  daysOfWeek: formValues.daysOfWeek || {},
                  eventInstanceType: formValues.eventInstanceType || null,
                  eventOccurrence: formValues.eventOccurrence || null,
                  dayOfTheWeek: formValues.dayOfTheWeek || null,
                  dayOfTheMonth: formValues.dayOfTheMonth || null,
                  serviceType: selectedPricedService?.serviceType,
                  material: fullConfiguredService.material || null,
                  measure: fullConfiguredService.measure || null,
                  method: fullConfiguredService.serviceMethod || null,
                  action: fullConfiguredService.serviceAction || null,
                  pricingPeriod: fullConfiguredService.pricingPeriod || null,
                  pricingZone: fullConfiguredService.pricingZone || null,
                  taxZone: fullConfiguredService.taxZone || null,
                  purchaseOrderNumber: fullConfiguredService.purchaseOrderNumber || '',
                  endDate: fullConfiguredService.endDate || null,
                  billingStartDate: fullConfiguredService.billingStartDate || null,
                  billingEndDate: fullConfiguredService.billingEndDate || null,
                  deactivateOnEndDate: fullConfiguredService.deactivateOnEndDate || false,
                  deactivate: fullConfiguredService.deactivate || false,
                }

                const formatted = formatFormData(updateData)
                return updateConfiguredService({
                  ...formatted,
                  id: publishResult.configuredServiceId,
                  skipWorkOrderInvalidation: true,
                }).unwrap()
              })
              .catch(handleError)
          }

          return publishResult
        }
        return undefined
      } catch (error) {
        handleError(error)
        return undefined
      } finally {
        setIsComplexSubmitting(false)
      }
    },
    [
      isSelectedPricedServiceOnRequest,
      onRequestAction,
      onSubmitHandler,
      publishDraftServiceMutation,
      createCSEvents,
      methods,
      recurrenceOptions,
      selectedPricedService,
      formatFormData,
      updateConfiguredService,
      fetchConfiguredService,
    ]
  )

  return {
    createDraftService,
    createAndPublishDraftService,
    isCreating,
    isPublishing,
    isUpdating,
    isComplexSubmitting,
  }
}
